import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { readMeetverseQuestionsApiV1QuestionsMeetverseGet } from '@api-client/api-client';
import {
  questionsMeetverseInfiniteKey,
  upsertQuestionEntities,
  resolveNextPageParam,
  type PageParam,
  type NormalizedPage,
} from '@lib/cache/questions';
import { useQuestionEntities } from './useQuestionEntities';
import { MEETVERSE_FEED_STALE_TIME_MS } from '@lib/feed/constants';
import { spliceJustPosted } from '@lib/feed/justPosted';
import { refreshInfiniteQuery } from '@utils/refreshInfiniteQuery';

const QUESTIONS_PER_PAGE = 20;

interface UseInfiniteMeetverseQuestionsOptions {
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
  refetchOnWindowFocus?: boolean;
  refetchInterval?: number | false;
  refetchIntervalInBackground?: boolean;
}

/**
 * Infinite scroll hook for Meetverse questions (visibility=ANYONE, event_id=NULL)
 *
 * AIDEV-NOTE: This hook is specifically for the Meetverse feed - questions visible to anyone
 * without event context. It uses the /questions/meetverse endpoint which filters for:
 * - visibility = ANYONE (Meetverse public)
 * - event_id = NULL (not tied to any event)
 *
 * Entities are stored in the entity store (questionEntityKey) so optimistic writes
 * from useQuestionInteractionsOptimistic propagate here via useQueries subscriptions
 * without any list-walking.
 */
export const useInfiniteMeetverseQuestions = ({
  enabled = true,
  staleTime = MEETVERSE_FEED_STALE_TIME_MS,
  gcTime = 10 * 60 * 1000,
  refetchOnWindowFocus = true,
  refetchInterval = false,
  refetchIntervalInBackground = false,
}: UseInfiniteMeetverseQuestionsOptions = {}) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => questionsMeetverseInfiniteKey(), []);

  const {
    data: idPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useInfiniteQuery<NormalizedPage>({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      const params: { cursor?: number; page?: number; per_page: number } = {
        per_page: QUESTIONS_PER_PAGE,
      };
      const typedPageParam = pageParam as PageParam | null;
      if (typedPageParam && 'cursor' in typedPageParam) {
        params.cursor = typedPageParam.cursor;
      } else if (typedPageParam && 'page' in typedPageParam) {
        params.page = typedPageParam.page;
      }

      const response = await readMeetverseQuestionsApiV1QuestionsMeetverseGet(params, { signal });
      const questions = response.data.data;

      // Guard: if cancelled while the request was in flight, skip the entity
      // store write so the response can't clobber an in-progress optimistic update.
      signal.throwIfAborted();

      // Populate entity store — mutations write here; list subscribers react automatically.
      upsertQuestionEntities(queryClient, questions);

      return {
        ids: questions.map(q => q.id),
        pagination: response.data.pagination,
        meta: response.data.meta,
      };
    },
    enabled,
    initialPageParam: null as PageParam | null,
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchInterval,
    refetchIntervalInBackground,
    getNextPageParam: (lastPage, allPages) =>
      resolveNextPageParam(lastPage, allPages, { hookName: 'useInfiniteMeetverseQuestions' }),
    // Just-posted splice (spec 1.3): pure select that keeps the posts this device just
    // created first in pages[0] for JUST_POSTED_SPLICE_MS, so a pull-to-refresh that
    // returns a server page without them (fan-out lag) cannot displace them. Outside
    // the window it returns `data` unchanged (same reference), keeping memoization
    // intact. Passed as a module-stable reference: React Query re-runs select on
    // Object.is identity of the function, so an inline arrow would defeat that
    // memoization (guarded by the identity test in __tests__).
    select: spliceJustPosted,
  });

  const ids = useMemo(() => idPages?.pages.flatMap(p => p.ids) ?? [], [idPages]);

  const questions = useQuestionEntities(ids, { gcTime });

  const refresh = useCallback(
    () => refreshInfiniteQuery(queryClient, queryKey),
    [queryClient, queryKey],
  );

  return {
    questions,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    refresh,
  };
};

export { QUESTIONS_PER_PAGE as MEETVERSE_QUESTIONS_PER_PAGE };
